import React, { useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle, ChevronDown, Clock } from "lucide-react";
import "./TasksWidget.css";
import { Button } from "react-bootstrap";
import { stripMarkdown } from "../../../utils/markdown";

const formatDue = (value) => {
  if (!value) return "No deadline";
  const diffMs = new Date(value).getTime() - Date.now();
  const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  if (days < 0) return "Due date passed";
  if (days === 0) return "Due today";
  if (days === 1) return "Due tomorrow";
  return `Due in ${days} days`;
};

const statusFromDeadline = (value) => {
  if (!value) return "IN PROGRESS";
  return new Date(value).getTime() < Date.now() ? "LATE" : "IN PROGRESS";
};

const difficultyClass = {
  easy: "diff-easy",
  medium: "diff-medium",
  hard: "diff-hard text-danger",
};

const filterOptions = [
  { value: "all", label: "All Status" },
  { value: "active", label: "In Progress" },
  { value: "late", label: "Late" },
];

const TasksWidget = ({ tasks = [] }) => {
  const [filter, setFilter] = useState("all");
  const [filterOpen, setFilterOpen] = useState(false);
  const filterSelectRef = useRef(null);

  const normalized = useMemo(() => {
    return tasks.map((task) => ({
      ...task,
      uiStatus: statusFromDeadline(task.deadline),
      dueText: formatDue(task.deadline),
    }));
  }, [tasks]);

  const filteredTasks = useMemo(() => {
    if (filter === "all") return normalized;
    if (filter === "late") return normalized.filter((task) => task.uiStatus === "LATE");
    return normalized.filter((task) => task.uiStatus !== "LATE");
  }, [filter, normalized]);

  const activeFilterLabel = useMemo(
    () => filterOptions.find((option) => option.value === filter)?.label || "All Status",
    [filter]
  );

  useEffect(() => {
    const onClickOutside = (event) => {
      if (!filterSelectRef.current?.contains(event.target)) {
        setFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  if (!tasks.length) {
    return (
      <div className="widget-container">
        <div className="widget-header-row">
          <h3 className="widget-title">
            <CheckCircle size={20} className="widget-icon" />
            Current Tasks
          </h3>
        </div>
        <div className="alert alert-light border">No tasks found for this track yet.</div>
      </div>
    );
  }

  return (
    <div className="widget-container">
      <div className="widget-header-row tasks-header-row">
        <h3 className="widget-title">
          <CheckCircle size={20} className="widget-icon" />
          Current Tasks
        </h3>
        <div className={`status-select-wrap ${filterOpen ? "open" : ""}`} ref={filterSelectRef}>
          <button
            type="button"
            className="status-select-btn"
            onClick={() => setFilterOpen((prev) => !prev)}
            aria-haspopup="listbox"
            aria-expanded={filterOpen}
          >
            <span>{activeFilterLabel}</span>
            <ChevronDown size={16} className="status-select-chevron" />
          </button>
          {filterOpen ? (
            <div className="status-select-menu" role="listbox" aria-label="Filter tasks by status">
              {filterOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`status-option ${filter === option.value ? "active" : ""}`}
                  onClick={() => {
                    setFilter(option.value);
                    setFilterOpen(false);
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      <div className="task-cards">
        {filteredTasks.slice(0, 3).map((task) => (
          <div key={task._id} className={`task-card ${task.uiStatus === "LATE" ? "late-card" : "in-progress-card"}`}>
            <div className="task-header">
              <div className="task-badges">
                <span className={`task-statusbadge ${task.uiStatus === "LATE" ? "badge-late" : "badge-progress"}`}>
                  {task.uiStatus}
                </span>
                <span className={`task-due ${task.uiStatus === "LATE" ? "text-danger" : "text-muted"}`}>
                  {task.dueText}
                </span>
              </div>
              <div className="task-points fw-bold">{task.points} pts</div>
            </div>

            <h4 className="task-title">{task.title}</h4>
            <p className="task-desc">{stripMarkdown(task.description) || "No description provided."}</p>

            <div className="task-footer">
              <div className="task-meta">
                <span className={`meta-item ${difficultyClass[task.difficulty] || "diff-medium"}`}>
                  {task.difficulty || "medium"}
                </span>
                <span className="meta-item">
                  <Clock size={14} className="me-1" />
                  {task.allowResubmission ? "Resubmission allowed" : "Single submission"}
                </span>
              </div>
              <Button className={`task-action-btn ${task.uiStatus === "LATE" ? "btn-dark" : "btn-primary-custom"}`}>
                Open
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TasksWidget;
